function getEligibilityUrl() {
    return String(import.meta.env.VITE_ELIGIBILITY_API_URL || "").trim();
}

function getEligibilityApiKey() {
    return String(import.meta.env.VITE_ELIGIBILITY_API_KEY || "").trim();
}

function normalizeDob(value) {
    const text = String(value || "").trim();
    const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
        return isoMatch[1];
    }
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? text : date.toISOString().slice(0, 10);
}

function normalizePhone(value) {
    if (value == null) {
        return "";
    }
    const raw = Array.isArray(value)
        ? String(value.find((x) => String(x ?? "").trim()) ?? "").trim()
        : String(value).trim();
    if (!raw || /^not\s+available$/i.test(raw) || /^unavailable$/i.test(raw)) {
        return "";
    }
    const first = raw.split(/\s+and\s+|[\n;,|]+/i)[0] || "";
    return first.replace(/\D/g, "");
}

function normalizeAddress(value) {
    if (value == null) {
        return "";
    }
    const raw = Array.isArray(value)
        ? String(value.find((x) => String(x ?? "").trim()) ?? "").trim()
        : String(value).trim();
    if (!raw || /^not\s+available$/i.test(raw) || /^unavailable$/i.test(raw)) {
        return "";
    }
    return raw.replace(/\s*\n\s*/g, ", ").replace(/\s+/g, " ").trim();
}

function normalizeEmpiId(row) {
    const value =
        row?.empiId ??
        row?.empi_id ??
        row?.empi ??
        row?.EMPI ??
        "";
    const text = String(value ?? "").trim();
    return text || null;
}

function normalizePayerName(row) {
    const payer =
        row?.payer_name ??
        row?.payerName ??
        row?.payer ??
        row?.health_plan_name ??
        "GVHC CCAH";
    if (typeof payer === "object") {
        return String(payer?.name || payer?.payerName || "").trim() || "GVHC CCAH";
    }
    return String(payer || "").trim() || "GVHC CCAH";
}

/**
 * Map UI/storage gender (M/F or male/female) to API values.
 * UI may display M/F; Waystar and related APIs expect "male" | "female".
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeGender(value) {
    const text = String(value ?? "").trim().toLowerCase();
    if (!text) {
        return "";
    }
    if (text === "m" || text === "male") {
        return "male";
    }
    if (text === "f" || text === "female") {
        return "female";
    }
    return text;
}

/**
 * Waystar eligibility POST body.
 * @returns {{
 *   memberDetails: {
 *     memberCin: string,
 *     memberFirstName: string,
 *     memberLastName: string,
 *     memberDob: string,
 *     memberGender: string,
 *     phone: string,
 *     Address: string,
 *     empiId: string|null
 *   },
 *   payerName: string
 * }}
 */
export function buildEligibilityPayload(row, cinFallback = "") {
    return {
        memberDetails: {
            memberCin: String(row?.cin || cinFallback || "").trim(),
            memberFirstName: String(row?.first_name || "").trim(),
            memberLastName: String(row?.last_name || "").trim(),
            memberDob: normalizeDob(row?.dob),
            memberGender: normalizeGender(row?.gender),
            phone: normalizePhone(row?.phone),
            Address: normalizeAddress(row?.address),
            empiId: normalizeEmpiId(row)
        },
        payerName: normalizePayerName(row)
    };
}

export async function checkMemberEligibility(row, cinFallback = "") {
    const url = getEligibilityUrl();
    if (!url) {
        throw new Error("Eligibility API URL is not configured.");
    }

    const apiKey = getEligibilityApiKey();
    if (!apiKey) {
        throw new Error("Eligibility API key is not configured.");
    }

    const payload = buildEligibilityPayload(row, cinFallback);
    const details = payload.memberDetails || {};
    const required = {
        memberCin: details.memberCin,
        memberFirstName: details.memberFirstName,
        memberLastName: details.memberLastName,
        memberDob: details.memberDob,
        memberGender: details.memberGender,
        payerName: payload.payerName
    };
    const missingFields = Object.entries(required)
        .filter(([, value]) => !value)
        .map(([key]) => key);
    if (missingFields.length > 0) {
        throw new Error(`Missing eligibility data: ${missingFields.join(", ")}`);
    }

    const response = await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-api-key": apiKey
        },
        body: JSON.stringify(payload)
    });

    const rawText = await response.text();
    let data = null;
    try {
        data = rawText ? JSON.parse(rawText) : null;
    } catch {
        data = null;
    }

    if (!response.ok) {
        const message =
            data?.message ||
            data?.error ||
            data?.detail ||
            data?.reason ||
            rawText ||
            `Eligibility request failed (${response.status})`;
        const error = new Error(message);
        error.status = response.status;
        throw error;
    }

    return data;
}
